import React from "react";
import { Link } from "react-router-dom";
import {
  useProfile,
  usePositionsData,
  formatAmount,
  getCurrencySymbol,
  useTaxEventsData,
  TaxEventItemClass,
  TaxType,
  CapitalTerm,
  useTaxSummary,
} from "@moonscape/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyWidget } from "@/components/ui/empty-widget";
import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { Callout, CoinMark, DHead, Fig, Lab, StatBand, Tag } from "@/components/ms";
import { FigLink, HeadLink, StepMark } from "./chrome";

export const TaxDashboardWidget = () => {
  const { profile, isQueryLoading } = useProfile();
  const { data: positionsData, isLoading: isPositionsLoading } = usePositionsData();
  const currentTaxYear = new Date().getFullYear() - 1; // filing for previous year

  // Fetch tax summary to check if there's a report
  const { data: taxSummary } = useTaxSummary({
    taxYear: currentTaxYear,
  });

  // Fetch recent tax events (top 3 for dashboard)
  const { taxEvents } = useTaxEventsData({
    page: 0,
    taxYear: currentTaxYear,
    taxType: TaxType.CAPITAL_GAIN,
    capitalTerm: CapitalTerm.ALL,
    onLoading: () => {},
  });

  const baseCurrency = profile?.baseCurrency || "USD";
  const currencySymbol = getCurrencySymbol(baseCurrency);
  const taxJurisdiction = profile?.taxJurisdiction;
  const hasLinkedExchangeAccount = profile?.hasLinkExchangeAccounts;
  const positions = positionsData?.balances || [];
  const positionsArray = Array.isArray(positions) ? positions : [];

  const recentEvents = (taxEvents?.items || []).slice(0, 3);

  // Calculate tax metrics
  const totalGains = positionsArray.reduce((sum, p) => {
    const gain = (p.currentValue || 0) - (p.costBasis || 0);
    return sum + (gain > 0 ? gain : 0);
  }, 0);

  const totalLosses = positionsArray.reduce((sum, p) => {
    const gain = (p.currentValue || 0) - (p.costBasis || 0);
    return sum + (gain < 0 ? Math.abs(gain) : 0);
  }, 0);

  // Tax loss harvesting opportunities (positions with losses > $100)
  const harvestingOpportunities = positionsArray.filter(p => {
    const loss = (p.currentValue || 0) - (p.costBasis || 0);
    return loss < -100;
  }).length;

  // Format currency
  const formatCurrency = (amount: number) => `${currencySymbol}${formatAmount(amount)}`;

  // Get tax year (filing for previous year)
  const filingYear = new Date().getFullYear() - 1;
  const taxYear = taxJurisdiction === "United Kingdom"
    ? `${filingYear}/${String(filingYear + 1).slice(-2)}`
    : filingYear.toString();

  // Get US standard deduction based on filing status (2024 values)
  const getUSStandardDeduction = () => {
    const filingStatus = profile?.taxFilingStatus;

    switch (filingStatus) {
      case "Single":
        return "$14,600";
      case "Married Filing Jointly":
      case "Qualifying Surviving Spouse":
        return "$29,200";
      case "Married Filing Separately":
        return "$14,600";
      case "Head of Household":
        return "$21,900";
      default:
        return "$14,600"; // Default to Single
    }
  };

  // Get jurisdiction-specific info
  const getJurisdictionInfo = () => {
    switch (taxJurisdiction) {
      case "United Kingdom":
      case "UK":
        return { allowance: "£3,000", deadline: "5 April", displayName: "UK" };
      case "United States":
      case "US":
        return { allowance: getUSStandardDeduction(), deadline: "15 April", displayName: "US" };
      case "Canada":
      case "CA":
        return { allowance: "50% inclusion", deadline: "30 April", displayName: "CA" };
      default:
        return { allowance: "—", deadline: "—", displayName: taxJurisdiction || "Not set" };
    }
  };

  const jurisdictionInfo = getJurisdictionInfo();

  // Loading state
  if (isQueryLoading || isPositionsLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Filing Overview" />
        <Skeleton className="mt-4 h-24 w-full" />
        <Skeleton className="mt-4 h-20 w-full" />
      </section>
    );
  }

  // Empty state: no tax jurisdiction configured
  if (!taxJurisdiction) {
    return (
      <section className="flex flex-col">
        <DHead label="Filing Overview" />
        <EmptyWidget
          icon={<WrenchScrewdriverIcon className="h-8 w-8" />}
          title="Configure tax settings"
          description="Set your country and accounting method to see your filing overview"
          action={{ label: "Set Up Tax Settings", to: "/quiz" }}
        />
      </section>
    );
  }

  // Get jurisdiction-specific tax forms
  const getTaxForms = () => {
    switch (taxJurisdiction) {
      case "United States":
      case "US":
        return [
          { title: "Form 8949", description: "Per-transaction detail" },
          { title: "Schedule D", description: "Capital gains summary" },
          { title: "Form 1040", description: "Income tax return" },
        ];
      case "United Kingdom":
      case "UK":
        return [
          { title: "UK Crypto Tax Report", description: "SA108 + SA100 summary" },
          { title: "SA108 Summary", description: "Capital gains tax summary" },
        ];
      case "Australia":
      case "AU":
        return [
          { title: "Capital Gains Report", description: "ATO myTax format" },
          { title: "Transaction Summary", description: "Excel format for ATO" },
        ];
      default:
        return [
          { title: "Capital Gains Report", description: "Comprehensive tax report" },
          { title: "Transaction Report", description: "Complete transaction history" },
        ];
    }
  };

  const taxForms = getTaxForms();

  // Check if there's any tax data/report available
  const hasReport = taxSummary && (
    taxSummary.capitalGains ||
    taxSummary.income ||
    taxSummary.contractIncome
  );

  // Calculate filing progress based on available data
  const hasCapitalGains = taxSummary?.capitalGains && taxSummary.capitalGains.disposals > 0;
  const hasIncome = taxSummary?.income && taxSummary.income.disposals > 0;
  const hasContractIncome = taxSummary?.contractIncome && taxSummary.contractIncome.disposals > 0;
  const hasTaxSettings = profile?.hasRequiredTaxInfo;
  const hasAnyTaxEvents = hasCapitalGains || hasIncome || hasContractIncome;

  // Define filing progress steps with completion status
  const filingSteps = [
    {
      id: 'connect-accounts',
      label: 'Connect exchange accounts',
      completed: hasLinkedExchangeAccount || false,
      inProgress: false,
    },
    {
      id: 'configure-tax-settings',
      label: 'Configure tax settings',
      completed: hasTaxSettings || false,
      inProgress: !hasTaxSettings && hasLinkedExchangeAccount,
    },
    {
      id: 'generate-tax-report',
      label: 'Generate tax report',
      completed: hasAnyTaxEvents || false,
      inProgress: hasTaxSettings && !hasAnyTaxEvents,
    },
    {
      id: 'review-gains-losses',
      label: 'Review gains and losses',
      completed: hasAnyTaxEvents || false,
      inProgress: false,
    },
    {
      id: 'download-forms',
      label: 'Download tax forms',
      completed: false,
      inProgress: hasAnyTaxEvents || false,
    },
  ];

  // Calculate overall status
  const completedSteps = filingSteps.filter(step => step.completed).length;
  const totalSteps = filingSteps.length;
  const isReadyForDownload = hasAnyTaxEvents;

  return (
    <section className="flex flex-col gap-[var(--gap)]">
      <div className="flex flex-col">
        <DHead
          label={`Filing Overview · ${jurisdictionInfo.displayName} ${taxYear}`}
          right={<HeadLink to="/tax/cgt">View details</HeadLink>}
        />

        <StatBand
          className="mt-3.5"
          items={[
            {
              label: "Deadline",
              value: jurisdictionInfo.deadline,
            },
            {
              label:
                taxJurisdiction === "United Kingdom" || taxJurisdiction === "UK"
                  ? "CGT allowance"
                  : "Std. deduction",
              value: jurisdictionInfo.allowance,
            },
            ...(hasReport
              ? [
                  {
                    label: "Unrealised gains",
                    value: formatCurrency(totalGains),
                    tone: "green" as const,
                  },
                  {
                    label: "Unrealised losses",
                    value: formatCurrency(totalLosses),
                    tone: "neg" as const,
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* Filing progress */}
      <div className="flex flex-col">
        <DHead label="Filing Progress" right={`${completedSteps}/${totalSteps} done`} />
        <div className="flex flex-col">
          {filingSteps.map((step) => (
            <Fig
              key={step.id}
              lead={
                <StepMark
                  state={step.completed ? "done" : step.inProgress ? "live" : "wait"}
                />
              }
              label={step.label}
              className={step.completed ? "" : "text-ms-mute"}
              tag={
                step.completed ? (
                  <Tag tone="pos">Done</Tag>
                ) : step.inProgress ? (
                  <Tag tone="live">In progress</Tag>
                ) : (
                  <Tag tone="neu">To do</Tag>
                )
              }
            />
          ))}
        </div>
        {isReadyForDownload && (
          <Callout className="mt-3.5">
            <b>Your forms are ready.</b> Every step the numbers depend on has run.
          </Callout>
        )}
      </div>

      {harvestingOpportunities > 0 && (
        <Callout tone="tan">
          <b>
            {harvestingOpportunities} loss-harvesting opportunit
            {harvestingOpportunities !== 1 ? "ies" : "y"} in your holdings.
          </b>{" "}
          Realising them this year would offset gains.{" "}
          <Link to="/tax/loss-harvesting">Review them →</Link>
        </Callout>
      )}

      {/* Recent tax events */}
      {recentEvents.length > 0 && (
        <div className="flex flex-col">
          <DHead
            label="Recent tax events"
            right={<HeadLink to="/tax/cgt">View all</HeadLink>}
          />
          <div className="flex flex-col">
            {recentEvents.map((event, index) => {
              const t = new TaxEventItemClass(event);
              return (
                <Fig
                  key={`${event.id || index}`}
                  lead={<CoinMark sym={t?.asset} />}
                  label={t.asset}
                  sub={`${formatAmount(t.quantity)} · ${t.formattedTime()}`}
                  value={formatAmount(t.gain, currencySymbol, true)}
                  tone={t.gain > 0 ? "green" : t.gain < 0 ? "neg" : "mute"}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Tax forms */}
      <div className="flex flex-col">
        <DHead label="Tax forms" right={<HeadLink to="/tax/cgt">View all</HeadLink>} />
        <Lab className="mt-3 block text-ms-dim">
          Ready to download and submit
        </Lab>
        <div className="mt-3 flex flex-col">
          {taxForms.map((form) => (
            <FigLink
              key={form.title}
              to="/tax/cgt"
              label={form.title}
              sub={form.description}
              tag={<Tag tone="pos">Get</Tag>}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
